import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from 'react-query'
import { notifications, useCurrentUser } from 'context'
import { listProjectTeachers, listTutors, shareProject } from '../../services/arcProjectsApi'
import type { ShareType } from '../../types'

/** Tutors list rarely changes during a session */
const TUTORS_STALE_TIME = 5 * 60 * 1000 // 5 minutes

export const useShareWithTeachers = (
  projectId: string,
  opened: boolean,
  onClose: () => void,
) => {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [shareTypes, setShareTypes] = useState<Record<string, ShareType>>({})
  const [selections, setSelections] = useState<Set<string>>(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Reset all local state when modal opens */
  useEffect(() => {
    if (opened) {
      setSearch('')
      setShareTypes({})
      setSelections(new Set())
      setError(null)
    }
  }, [opened])

  const { data: tutors = [], isLoading: isLoadingTutors } = useQuery(
    ['arc-tutors'],
    listTutors,
    { enabled: opened, staleTime: TUTORS_STALE_TIME },
  )

  /** Fetch project teachers (author + co-teachers) to identify existing co-teachers */
  const { data: projectTeachers = [] } = useQuery(
    ['arc-project-teachers', projectId],
    () => listProjectTeachers(projectId),
    { enabled: opened && !!projectId, staleTime: 0 },
  )

  /** Tutor IDs of existing co-teachers (non-main teachers) */
  const coTeacherTutorIds = useMemo(() =>
    new Set(projectTeachers.filter(pt => !pt.mainTeacher).map(pt => pt.id)),
  [projectTeachers])

  /** Exclude the current user from the list (email may be in email or username field) */
  const currentUserEmail = currentUser?.email || currentUser?.username
  const availableTutors = useMemo(() =>
    tutors.filter(tutor => tutor.email !== currentUserEmail),
  [tutors, currentUserEmail])

  const selectedIds = useMemo(() => Array.from(selections), [selections])

  const filteredTutors = useMemo(() =>
    availableTutors.filter(tutor =>
      `${tutor.firstName} ${tutor.lastName}`
        .toLowerCase()
        .includes(search.toLowerCase().trim()),
    ),
  [availableTutors, search])

  const filteredIds = useMemo(() => filteredTutors.map(t => t.id), [filteredTutors])

  const isCoTeacher = useCallback((tutorId: string) =>
    coTeacherTutorIds.has(tutorId),
  [coTeacherTutorIds])

  const isAllSelected = filteredIds.length > 0 && filteredIds.every(id => selections.has(id))
  const isIndeterminate = !isAllSelected && filteredIds.some(id => selections.has(id))

  const handleSelectAll = useCallback(() => {
    setSelections(prev => {
      const allSelected = filteredIds.every(id => prev.has(id))
      const next = new Set(prev)
      if (allSelected) {
        filteredIds.forEach(id => next.delete(id))
      } else {
        filteredIds.forEach(id => next.add(id))
      }
      return next
    })
  }, [filteredIds])

  const toggleTutor = useCallback((tutorId: string) => {
    setSelections(prev => {
      const next = new Set(prev)
      if (next.has(tutorId)) {
        next.delete(tutorId)
      } else {
        next.add(tutorId)
      }
      return next
    })
  }, [])

  const getShareType = useCallback((tutorId: string): ShareType =>
    shareTypes[tutorId] || 'copy',
  [shareTypes])

  const setType = useCallback((tutorId: string, type: string | null) => {
    if (!type) return
    setShareTypes(prev => ({ ...prev, [tutorId]: type as ShareType }))
  }, [])

  const clearError = useCallback(() => setError(null), [])

  const handleSubmit = useCallback(async () => {
    if (selectedIds.length === 0) {
      setError(t('arc_projects.share_select_teachers_validation'))
      return
    }

    setIsSubmitting(true)
    setError(null)

    const copyIds = selectedIds.filter(id => getShareType(id) === 'copy')
    const coTeacherIds = selectedIds.filter(id => getShareType(id) === 'co_teacher')

    try {
      if (copyIds.length > 0) {
        const result = await shareProject(projectId, copyIds, 'copy')
        if (!result.success) throw new Error(result.error || t('arc_projects.share_error'))
      }
      if (coTeacherIds.length > 0) {
        const result = await shareProject(projectId, coTeacherIds, 'co_teacher')
        if (!result.success) throw new Error(result.error || t('arc_projects.share_error'))
      }

      await Promise.all([
        queryClient.invalidateQueries(['arc-projects']),
        queryClient.invalidateQueries(['arc-tutors']),
        queryClient.invalidateQueries(['arc-project-teachers', projectId]),
      ])

      notifications.show({
        title: t('arc_projects.share_success_title'),
        message: t('arc_projects.share_success_message'),
        color: 'green',
      })
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('arc_projects.share_partial_error'))
    } finally {
      setIsSubmitting(false)
    }
  }, [selectedIds, getShareType, projectId, t, queryClient, onClose])

  const allShareTypeData = useMemo(() => [
    {
      value: 'copy',
      label: t('arc_projects.share_as_copy'),
      description: t('arc_projects.share_as_copy_description'),
      icon: 'copy',
    },
    {
      value: 'co_teacher',
      label: t('arc_projects.share_add_co_teacher'),
      description: t('arc_projects.share_add_co_teacher_description'),
      icon: 'co_teacher',
    },
  ], [t])

  const copyOnlyData = useMemo(() =>
    allShareTypeData.filter(d => d.value === 'copy'),
  [allShareTypeData])

  const getShareTypeData = useCallback((tutorId: string) =>
    coTeacherTutorIds.has(tutorId) ? copyOnlyData : allShareTypeData,
  [coTeacherTutorIds, copyOnlyData, allShareTypeData])

  const hasTutors = availableTutors.length > 0

  return {
    search,
    setSearch,
    selectedIds,
    filteredTutors,
    isLoadingTutors,
    hasTutors,
    isAllSelected,
    isIndeterminate,
    isSubmitting,
    error,
    getShareTypeData,
    selections,
    getShareType,
    isCoTeacher,
    handleSelectAll,
    toggleTutor,
    setType,
    handleSubmit,
    clearError,
  }
}
